/**
 * The request pipeline both consoles use.
 *
 * Hoisted from two hand-rolled copies (vista#671). They had already drifted in
 * a way that mattered: the org client learned to omit `Content-Type` on a
 * body-less request (vista#380, after an Axum `Option<Json<T>>` extractor
 * returned 415 on a zero-byte POST) and the admin client did not — while the
 * admin console's own `POST /auth/session` and `POST /auth/session/refresh`
 * send no body at all, and two of its DELETEs pass `null`.
 *
 * Everything a caller can vary is a parameter; everything else is the same for
 * both, which is the point.
 */

/** 10 seconds. Long enough for a cold Worker, short enough to fail a hung one. */
export const DEFAULT_TIMEOUT_MS = 10_000;

/**
 * Build a client and its session-token store.
 *
 * @param apiBase Origin the API is served from. Empty/undefined means
 *   same-origin. The org dashboard is cross-origin by deployment (dashboard on
 *   `{slug}.vista-cloud.io`, API on `api.vista-cloud.io`); the admin console is
 *   same-origin (the admin worker is bound to `admin.vista-cloud.io/api/*`).
 *   Both paths are handled here so neither console has to know about the other.
 * @param prefix Path prefix every request is under. Both use `/api/v1`.
 * @param onResponse Optional per-response hook, called with the `Response`
 *   before the body is read. The org dashboard uses it to drive the
 *   stale-data banner from `X-Vista-Stale`; the admin console has no such
 *   surface, and passing a hook is cheaper than sharing a store neither app
 *   agrees on.
 */
export function createApiClient({
  apiBase,
  prefix = '/api/v1',
  timeoutMs = DEFAULT_TIMEOUT_MS,
  onResponse,
} = {}) {
  const base = apiBase || (typeof window !== 'undefined' ? window.location.origin : '');

  // The VISTA session JWT lives in memory only — never localStorage, never a
  // cookie — so a stolen token cannot outlive the tab.
  let vistaToken = null;

  function setVistaToken(token) {
    vistaToken = token;
  }

  function getVistaToken() {
    return vistaToken;
  }

  /**
   * A token usable for a WebSocket, which cannot carry custom headers.
   *
   * Falls back to the CF Access cookie so a socket can still authenticate
   * before the VISTA session exists.
   */
  function getWsToken() {
    if (vistaToken) return vistaToken;
    if (typeof document === 'undefined') return null;
    const cf = document.cookie.match(/CF_Authorization=([^;]+)/);
    return cf ? cf[1] : null;
  }

  async function request(method, path, { params, body } = {}) {
    const url = new URL(`${prefix}${path}`, base);

    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value != null) url.searchParams.set(key, value);
      }
    }

    const sameOrigin =
      typeof window === 'undefined' ? true : url.origin === window.location.origin;

    // vista#380: no body, no `Content-Type`. Sending `application/json` on a
    // zero-byte POST tripped Axum's `Option<Json<T>>` extractor (vista#349 —
    // proxy-adopt started 415ing when the dashboard moved to body-less POSTs).
    // The agent side was hardened in PR #377, but a correct client should not
    // be sending the wrong header either, and the admin console's session
    // calls are exactly this shape.
    const headers = body != null ? { 'Content-Type': 'application/json' } : {};

    if (vistaToken) headers['Authorization'] = `Bearer ${vistaToken}`;

    // Cross-origin: the CF_Authorization cookie is scoped to the dashboard
    // subdomain, not the API domain, so it is not sent automatically. Read it
    // and forward it as the header CF Access also accepts.
    if (!sameOrigin && typeof document !== 'undefined') {
      const cf = document.cookie.match(/CF_Authorization=([^;]+)/);
      if (cf) headers['Cf-Access-Jwt-Assertion'] = cf[1];
    }

    const options = {
      method,
      headers,
      // `include` so the CF cookie reaches the API domain at all.
      credentials: sameOrigin ? 'same-origin' : 'include',
    };
    if (body != null) options.body = JSON.stringify(body);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    options.signal = controller.signal;

    let res;
    try {
      res = await fetch(url, options);
    } catch (err) {
      if (err.name === 'AbortError') {
        // A distinct message and `status = 0`, so a caller can tell "we never
        // heard back" from "the server said no".
        const timeoutErr = new Error('Request timed out — please try again');
        timeoutErr.status = 0;
        throw timeoutErr;
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }

    const decorate = onResponse ? onResponse(res) : undefined;

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ error: res.statusText }));
      const err = new Error(errBody.error || `API ${method} ${path} failed (${res.status})`);
      err.status = res.status;
      err.body = errBody;
      throw err;
    }

    if (res.status === 204) return null;
    const data = await res.json();
    return typeof decorate === 'function' ? decorate(data) : data;
  }

  const api = {
    get: (path, params) => request('GET', path, { params }),
    post: (path, body, params) => request('POST', path, { params, body }),
    put: (path, body, params) => request('PUT', path, { params, body }),
    patch: (path, body, params) => request('PATCH', path, { params, body }),
    // vista#648: DELETE almost always needs query params (e.g. `agent_id` for
    // agent-scoped routes) and rarely a body. The (path, body, params) argument
    // order mirrors post/put, which makes passing `{agent_id}` in the body slot
    // an easy slip — it never reaches the query string and the agent-scoped
    // route 400s with "agent_id required" (broke proxy + alert-rule delete).
    // Self-heal: a DELETE handed an `agent_id`-bearing object in the body slot
    // with no explicit params treats it as params. `agent_id` is never a valid
    // DELETE body field, so this cannot clobber a legitimate body (e.g. the
    // tunnel delete's `{ endpoint_url }`). Prefer the explicit
    // `api.delete(path, null, { params })`.
    delete: (path, body, params) => {
      if (params === undefined && body && typeof body === 'object' && 'agent_id' in body) {
        params = body;
        body = null;
      }
      return request('DELETE', path, { params, body });
    },
  };

  return { api, request, setVistaToken, getVistaToken, getWsToken };
}
